import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import AuthContext from "../Context/AuthContext";

function Banner({ type = "info", children, className = "" }) {
  const styles = {
    success: "border-green-200 bg-green-50 text-green-700",
    error: "border-red-200 bg-red-50 text-red-700",
    info: "border-slate-200 bg-slate-50 text-slate-700",
  }[type];
  return (
    <div className={`rounded-xl border p-4 text-sm ${styles} ${className}`}>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      {title && <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>}
      {children}
    </section>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3">
      <div className="sm:w-48 shrink-0 text-sm text-slate-600">{label}</div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function formatThousands(val) {
  if (val === "" || val == null) return "";
  return String(val).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

// Numeric input
function NumericInput({ value, onChange, placeholder = "Enter number", allowDecimal = false }) {
  return (
    <input
      type="text"
      inputMode={allowDecimal ? "decimal" : "numeric"}
      placeholder={placeholder}
      value={formatThousands(value)}
      onChange={(e) => {
        const raw = e.target.value.replace(/\./g, "");
        const re = allowDecimal ? /^\d*([.,]\d*)?$/ : /^\d*$/;
        if (raw === "" || re.test(raw)) onChange(raw);
      }}
      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
    />
  );
}

const platformsAccount = ["Google", "Meta", "LinkedIn"];
const platformsCampaign = ["Google", "Meta"];

// kural tanımları (backend’le birebir aynı isimler)
const RULES = {
  Google: [
    { name: "Budget", inputType: "number" },
    {
      name: "Taxonomy",
      inputType: "dropdown",
      options: [
        "BrandName_Platform_CampaignName_Objective_StartDate_EndDate",
        "BrandName|Platform|CampaignName|Objective|StartDate|EndDate",
        "BrandName-Platform-CampaignName-Objective-StartDate-EndDate",
      ],
    },
    { name: "Target GBM budget", inputType: "number" },
  ],
  Meta: [
    { name: "Budget", inputType: "number" },
    {
      name: "Taxonomy",
      inputType: "dropdown",
      options: [
        "BrandName_Platform_CampaignName_Objective_StartDate_EndDate",
        "BrandName|Platform|CampaignName|Objective|StartDate|EndDate",
        "BrandName-Platform-CampaignName-Objective-StartDate-EndDate",
      ],
    },
    { name: "A/B Testing", inputType: "radio", options: ["Yes", "No"] },
    {
      name: "Budget Type",
      inputType: "dropdown",
      options: ["Daily Budget", "Lifetime Budget"],
    },
    { name: "Ad Set Budget", inputType: "number" },
  ],
  LinkedIn: [
    { name: "Budget", inputType: "number" },
    {
      name: "Budget Type",
      inputType: "dropdown",
      options: ["Daily Budget", "Lifetime Budget"],
    },
    {
      name: "Taxonomy",
      inputType: "dropdown",
      options: [
        "BrandName_Platform_CampaignName_Objective_StartDate_EndDate",
        "BrandName|Platform|CampaignName|Objective|StartDate|EndDate",
        "BrandName-Platform-CampaignName-Objective-StartDate-EndDate",
      ],
    },
  ],
};

export default function DropdownForm() {
  const { userData } = useContext(AuthContext);

  const [selectedMethod, setSelectedMethod] = useState(""); // Account | Campaign
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [campaignName, setCampaignName] = useState("");

  const [activeRules, setActiveRules] = useState([]); // ["Budget", ...]
  const [ruleValues, setRuleValues] = useState({});   // { Budget: "100", ... }

  const [accounts, setAccounts] = useState([]);
  const [userAccounts, setUserAccounts] = useState([]); // kullanıcıya tanımlı accountlar
  const [selectedCampaignAccount, setSelectedCampaignAccount] = useState("");
  const [complete, setComplete] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // ✨ yeni: backend yanıt detayları
  const [conflicts, setConflicts] = useState([]); // [{ruleDescription, existing, attempted}]
  const [createdCount, setCreatedCount] = useState(null);
  const [skippedCount, setSkippedCount] = useState(null);

  // hesaplar
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const name = userData?.user?.user?.name;
        const [allRes, userRes] = await Promise.all([
          axios.get("https://act-api.vercel.app/accounts"),
          name
            ? axios.post("https://act-api.vercel.app/findamember", { name })
            : Promise.resolve({ data: [] }),
        ]);
        if (!mounted) return;
        setAccounts(Array.isArray(allRes.data) ? allRes.data : []);
        setUserAccounts(Array.isArray(userRes.data) ? userRes.data : []);
      } catch {
        setErrorMsg("Hesaplar yüklenemedi. Lütfen sayfayı yenileyin.");
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [userData?.user?.user?.name]);

  // method/platform değişince seçimleri sıfırla
  function resetSelections({ methodChanged = false, platformChanged = false } = {}) {
    if (methodChanged) {
      setSelectedPlatform("");
      setSelectedAccount("");
      setCampaignName("");
      setSelectedCampaignAccount("");
    }
    if (platformChanged) {
      setSelectedAccount("");
      setSelectedCampaignAccount("");
    }
    setActiveRules([]);
    setRuleValues({});
    setComplete(false);
    setErrorMsg("");
    // ✨ yeni temizlemeler
    setConflicts([]);
    setCreatedCount(null);
    setSkippedCount(null);
  }

  const availableRules = useMemo(() => {
    if (!selectedPlatform) return [];
    const base = RULES[selectedPlatform] || [];
    if (selectedMethod === "Campaign") {
      return base.filter(
        (r) =>
          r.name !== "Taxonomy" && r.name !== "Ad Group Name" && r.name !== "Ad Set Name"
      );
    }
    return base;
  }, [selectedPlatform, selectedMethod]);

  function handleRuleClick(ruleName) {
    setActiveRules((prev) => {
      if (prev.includes(ruleName)) {
        const next = prev.filter((r) => r !== ruleName);
        const { [ruleName]: _, ...rest } = ruleValues;
        setRuleValues(rest);
        return next;
      } else {
        setRuleValues((rv) => ({ ...rv, [ruleName]: ruleDefaultValue(ruleName) }));
        return [...prev, ruleName];
      }
    });
    setComplete(false);
    setConflicts([]);
    setCreatedCount(null);
    setSkippedCount(null);
  }

  function ruleDefaultValue(ruleName) {
    const def = availableRules.find((r) => r.name === ruleName);
    if (!def) return "";
    if (def.inputType === "radio") return def.options?.[0] || "";
    if (def.inputType === "dropdown") return "";
    if (def.inputType === "checkbox") return false;
    return ""; // text/number
  }

  function handleRuleValueChange(ruleName, value) {
    setRuleValues((prev) => ({ ...prev, [ruleName]: value }));
    setConflicts([]);
    setCreatedCount(null);
    setSkippedCount(null);
  }

  function toNumberOrNull(v) {
    return v === "" ? null : Number(String(v).replace(",", "."));
  }

  function buildPayloadRules() {
    const out = {};
    for (const rName of Object.keys(ruleValues)) {
      const def = availableRules.find((r) => r.name === rName);
      const v = ruleValues[rName];
      if (!def) {
        out[rName] = v;
        continue;
      }
      if (def.inputType === "number") {
        out[rName] = toNumberOrNull(v);
      } else {
        out[rName] = v;
      }
    }
    return out;
  }

  // doğrulama
  function validate() {
    if (!selectedMethod) return "Please select a method.";
    if (!selectedPlatform) return "Please select a platform.";
    if (selectedMethod === "Account" && !selectedAccount) return "Please select an account.";
    if (selectedMethod === "Campaign" && !campaignName.trim())
      return "Please enter a campaign name.";
    if (selectedMethod === "Campaign" && !selectedCampaignAccount)
      return "Please select an account for this campaign.";
    if (activeRules.length === 0) return "Please select at least one rule.";
    for (const r of activeRules) {
      const def = availableRules.find((x) => x.name === r);
      const val = ruleValues[r];
      if (!def) continue;
      if (def.inputType === "checkbox") continue;
      if (def.inputType === "number" && val === "") return `Please enter a value for "${r}".`;
      if ((def.inputType === "text" || def.inputType === "dropdown" || def.inputType === "radio") && String(val) === "") {
        return `Please set a value for "${r}".`;
      }
    }
    return "";
  }

  async function submit() {
    setComplete(false);
    setErrorMsg("");
    setConflicts([]);
    setCreatedCount(null);
    setSkippedCount(null);

    const v = validate();
    if (v) {
      setErrorMsg(v);
      return;
    }

    setSaving(true);
    try {
      const ruleValuesTransformed = buildPayloadRules();

      if (selectedMethod === "Account") {
        const res = await axios.post("https://act-api.vercel.app/rules", {
          ruleValues: ruleValuesTransformed,
          selectedPlatform,
          selectedAccount,
        });

        // ✅ Başarı
        setComplete(true);
        setCreatedCount(res?.data?.created ?? null);
        setSkippedCount(res?.data?.skipped ?? null);

        // formu kısmen sıfırla (platform & method kalsın istersen)
        setActiveRules([]);
        setRuleValues({});
        setSelectedAccount("");
      } else {
        const userid = userData?.user?.user?._id;
        const res = await axios.post("https://act-api.vercel.app/rulesCampaign", {
          ruleValues: ruleValuesTransformed,
          selectedPlatform,
          campaignName,
          userid,
          account: selectedCampaignAccount,
        });
        setComplete(true);
        setCreatedCount(res?.data?.created ?? null);
        setSkippedCount(res?.data?.skipped ?? null);
        setActiveRules([]);
        setRuleValues({});
        setCampaignName("");
        setSelectedCampaignAccount("");
      }
    } catch (e) {
      const status = e?.response?.status;
      const payload = e?.response?.data;
      const msg = payload?.hata || payload?.error || "Kaydetme başarısız oldu. Lütfen tekrar deneyin.";

      if (status === 409 && Array.isArray(payload?.conflicts)) {
        // ❗ Çakışma: aynı account + aynı description altında farklı rule
        setErrorMsg(msg);
        setConflicts(payload.conflicts);
      } else {
        setErrorMsg(msg);
      }
    } finally {
      setSaving(false);
    }
  }

  const canSubmit = !validate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-800">
      {/* Üst başlık */}
      <div className="sticky top-0 z-10 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/50 border-b border-slate-200">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 py-3 flex items-center justify-between">
          <h1 className="text-lg font-semibold">Create Rules</h1>
          <button
            onClick={submit}
            disabled={saving || !canSubmit}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Submit"}
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-8 space-y-6">
        {complete && (
          <Banner type="success">
            You added rules.
            {(createdCount != null || skippedCount != null) && (
              <div className="mt-1 text-xs">
                {createdCount != null && <span> Created: <b>{createdCount}</b>.</span>}
                {skippedCount != null && <span> Skipped: <b>{skippedCount}</b>.</span>}
              </div>
            )}
          </Banner>
        )}

        {errorMsg && <Banner type="error">{errorMsg}</Banner>}

        {conflicts.length > 0 && (
          <Banner type="error" className="mt-[-12px]">
            <div className="font-medium mb-1">Conflicting rules:</div>
            <ul className="list-disc pl-5 space-y-1">
              {conflicts.map((c, i) => (
                <li key={i}>
                  <span className="font-semibold">{c.ruleDescription}</span>: existing "
                  <span className="font-mono">{String(c.existing)}</span>", attempted "
                  <span className="font-mono">{String(c.attempted)}</span>"
                </li>
              ))}
            </ul>
          </Banner>
        )}

        {/* Metot & Platform */}
        <Section title="Scope">
          <Row label="Select Method">
            <select
              value={selectedMethod}
              onChange={(e) => {
                setSelectedMethod(e.target.value);
                resetSelections({ methodChanged: true });
              }}
              className="w-full py-2 px-3 border rounded-md text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
            >
              <option value="">Select a method</option>
              <option value="Campaign">Campaign</option>
              <option value="Account">Account</option>
            </select>
          </Row>

          <Row label="Select Platform">
            <select
              value={selectedPlatform}
              onChange={(e) => {
                setSelectedPlatform(e.target.value);
                resetSelections({ platformChanged: true });
              }}
              className="w-full py-2 px-3 border rounded-md text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
              disabled={!selectedMethod}
            >
              <option value="">Select a platform</option>
              {(selectedMethod === "Campaign" ? platformsCampaign : platformsAccount).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Row>

          {/* Account / Campaign alanları */}
          {selectedMethod === "Account" && (
            <Row label="Select Account">
              <select
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className="w-full py-2 px-3 border rounded-md text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                disabled={!selectedPlatform}
              >
                <option value="">Select an account</option>
                {loading ? (
                  <option>Loading…</option>
                ) : (
                  accounts.map((acc) => (
                    <option key={acc.name} value={acc.name}>
                      {acc.name} {acc.company ? `— ${acc.company}` : ""}
                    </option>
                  ))
                )}
              </select>
            </Row>
          )}

          {selectedMethod === "Campaign" && (
            <>
              <Row label="Campaign Name">
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  className="w-full py-2 px-3 border rounded-md text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                  placeholder="Enter campaign name"
                  disabled={!selectedPlatform}
                />
              </Row>

              <Row label="Select Account">
                <select
                  value={selectedCampaignAccount}
                  onChange={(e) => setSelectedCampaignAccount(e.target.value)}
                  className="w-full py-2 px-3 border rounded-md text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                  disabled={!selectedPlatform}
                >
                  <option value="">Select an account</option>
                  {loading ? (
                    <option>Loading…</option>
                  ) : (
                    userAccounts.map((acc) => (
                      <option key={acc._id || acc.name} value={acc.name}>
                        {acc.name}{acc.company ? ` — ${acc.company}` : ""}
                      </option>
                    ))
                  )}
                </select>
              </Row>
            </>
          )}
        </Section>

        {/* Kurallar */}
        {selectedPlatform && (
          <Section title="Rules">
            <Row label="Select Rules">
              <div className="flex flex-wrap gap-2">
                {availableRules.map((rule) => (
                  <button
                    key={rule.name}
                    type="button"
                    onClick={() => handleRuleClick(rule.name)}
                    className={`py-2 px-4 rounded-full border text-sm ${
                      activeRules.includes(rule.name)
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-blue-600 border-blue-600"
                    }`}
                  >
                    {rule.name}
                  </button>
                ))}
                {activeRules.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setActiveRules([]);
                      setRuleValues({});
                      setComplete(false);
                      setConflicts([]);
                      setCreatedCount(null);
                      setSkippedCount(null);
                    }}
                    className="py-2 px-3 rounded-full border text-sm bg-white text-slate-600 border-slate-300"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </Row>

            {/* Seçilen kuralların inputları */}
            {activeRules.map((ruleName) => {
              const cfg = availableRules.find((r) => r.name === ruleName);
              if (!cfg) return null;

              return (
                <Row key={ruleName} label={ruleName}>
                  {cfg.inputType === "text" && (
                    <input
                      type="text"
                      value={ruleValues[ruleName] ?? ""}
                      onChange={(e) => handleRuleValueChange(ruleName, e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                    />
                  )}

                  {cfg.inputType === "number" && (
                    <NumericInput
                      value={ruleValues[ruleName] ?? ""}
                      onChange={(v) => handleRuleValueChange(ruleName, v)}
                      placeholder="Enter value"
                      allowDecimal={false}
                    />
                  )}

                  {cfg.inputType === "checkbox" && (
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!ruleValues[ruleName]}
                        onChange={(e) => handleRuleValueChange(ruleName, e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>Enabled</span>
                    </label>
                  )}

                  {cfg.inputType === "radio" && (
                    <div className="flex items-center gap-4">
                      {cfg.options.map((opt) => (
                        <label key={opt} className="inline-flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name={ruleName}
                            value={opt}
                            checked={ruleValues[ruleName] === opt}
                            onChange={(e) => handleRuleValueChange(ruleName, e.target.value)}
                            className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {cfg.inputType === "dropdown" && (
                    <select
                      value={ruleValues[ruleName] ?? ""}
                      onChange={(e) => handleRuleValueChange(ruleName, e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                    >
                      <option value="">Select an option</option>
                      {cfg.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  )}
                </Row>
              );
            })}
          </Section>
        )}

        {/* Alt aksiyon */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={submit}
            disabled={saving || !canSubmit}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}
